import { Component, ElementRef, Inject, Input, PLATFORM_ID, ViewChild, type OnInit } from '@angular/core';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { CmsService } from '../../services/cms.service';
import { MatStepperModule } from '@angular/material/stepper';
import { MatIconModule } from '@angular/material/icon';
import { isPlatformBrowser } from '@angular/common';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { UtilService } from '../../services/util.service';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { StorageService } from '../../services/storage.service';
import { StorageEnum } from '../../enums/storage.enum';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { Router } from '@angular/router';
import { Nav } from '../../enums/nav.enum';

@Component({
  selector: 'app-ssm-stepper',
  imports: [MatStepperModule, MatIconModule],
  templateUrl: './ssm-stepper.component.html',
  styleUrl: './ssm-stepper.component.scss',
})
export class SsmStepperComponent implements OnInit {
  @ViewChild('stepper', { read: ElementRef }) stepperElement?: ElementRef<HTMLElement>;
  @Input() step!: number;
  @Input() process!: string;
  pageReady = false;
  pageLabels: any;
  startingIndex = 0;
  isMobile = false;
  isBrowser = false;
  stepLabels: string[] = [];

  constructor(
    private readonly cms: CmsService,
    // eslint-disable-next-line @typescript-eslint/ban-types
    @Inject(PLATFORM_ID) readonly platformId: Object,
    private readonly utils: UtilService,
    private readonly storage: StorageService,
    private readonly router: Router,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    if (this.isBrowser) {
      this.isMobile = this.utils.isMobile(576);
    }
  }

  ngOnInit(): void {
    if (this.isBrowser) {
      window.addEventListener('resize', () => {
        this.isMobile = this.utils.isMobile(576);
        this.applyHeaderAccessibilityPatch();
      });
      this.cms.getContent('oas-ssm-stepper', 'Stepper Labels').subscribe((data: any) => {
        this.pageLabels = data;
        this.setStepLabels();
        this.startingIndex = this.step - 1;
        this.pageReady = true;
        this.applyHeaderAccessibilityPatch();
      });
      if (this.step > 1) {
        if (!this.storage.getLocal(StorageEnum.isProcess)) {
          const process = this.router.url.split('/');
          const processData = {
            Url: this.router.url,
            ProcessType: process[2],
            Step: process[3] ? process[3] : '',
            StartedProcess: true,
          };
          this.storage.setLocal(StorageEnum.isProcess, JSON.stringify(processData));
        }
      } else {
        if (this.storage.getLocal(StorageEnum.isProcess)) {
          const isProcess = JSON.parse(this.storage.getLocal(StorageEnum.isProcess));
          if (this.router.url.includes(isProcess.Url)) {
            const acct = this.storage.getSession(StorageEnum.LoginResponse);
            if (acct.TotalNumAccounts > 1) {
              void this.router.navigateByUrl(Nav.MultiAccountSummary);
              this.storage.removeLocal(StorageEnum.isProcess);
            } else {
              void this.router.navigateByUrl(Nav.AccountSummary);
              this.storage.removeLocal(StorageEnum.isProcess);
            }
          } else {
            this.storage.removeLocal(StorageEnum.isProcess);
          }
        }
      }
    }
  }

  private setStepLabels(): void {
    if (this.process === 'move') {
      this.stepLabels = [
        this.pageLabels.stopService,
        this.pageLabels.newAddress,
        this.pageLabels.chooseService,
        this.pageLabels.choosePlan,
      ];
      return;
    }

    if (this.process === 'start') {
      this.stepLabels = [this.pageLabels.newAddress, this.pageLabels.chooseService, this.pageLabels.choosePlan];
      return;
    }

    if (this.process === 'reconnect') {
      this.stepLabels = [this.pageLabels.chooseService, this.pageLabels.choosePlan];
      return;
    }

    this.stepLabels = [];
  }

  private applyHeaderAccessibilityPatch(): void {
    if (!this.isBrowser || !this.pageReady) {
      return;
    }

    // Wait for mat-step-header elements to render before overriding attributes.
    setTimeout(() => {
      const stepperNativeElement = this.stepperElement?.nativeElement;
      if (!stepperNativeElement) {
        return;
      }

      const stepHeaders = stepperNativeElement.querySelectorAll<HTMLElement>('mat-step-header');
      const totalSteps = stepHeaders.length;
      stepHeaders.forEach((header, index) => {
        const isCurrentStep = index === this.startingIndex;
        const isCompletedStep = index < this.startingIndex;
        const isKeyboardNavigable = isCompletedStep;
        const canNavigateToStep = isCompletedStep;

        header.setAttribute('role', 'button');
        header.setAttribute('tabindex', isKeyboardNavigable ? '0' : '-1');
        header.setAttribute('aria-disabled', isKeyboardNavigable ? 'false' : 'true');
        header.removeAttribute('aria-controls');
        header.removeAttribute('aria-selected');
        header.style.cursor = canNavigateToStep ? 'pointer' : 'default';

        if (canNavigateToStep) {
          header.onclick = () => {
            this.navigateToStep(index);
          };

          header.onkeydown = (event: KeyboardEvent) => {
            if (event.key !== 'Enter' && event.key !== ' ') {
              return;
            }

            event.preventDefault();
            this.navigateToStep(index);
          };
        } else {
          header.onclick = null;
          header.onkeydown = null;
        }

        if (isCurrentStep) {
          header.setAttribute('aria-current', 'step');
        } else {
          header.removeAttribute('aria-current');
        }

        const stepLabel = this.stepLabels[index];
        if (!stepLabel) {
          return;
        }

        const stepContext = `Step ${index + 1} of ${totalSteps}`;

        if (isCurrentStep) {
          header.setAttribute('aria-label', `${stepLabel}. ${stepContext}`);
          return;
        }

        if (isCompletedStep) {
          header.setAttribute('aria-label', `${stepLabel}. ${stepContext}. Completed step`);
          return;
        }

        header.setAttribute('aria-label', `${stepLabel}. ${stepContext}. Upcoming step`);
      });
    });
  }

  private navigateToStep(stepIndex: number): void {
    const targetRoute = this.getStepRoute(stepIndex);
    if (!targetRoute || targetRoute === this.router.url) {
      return;
    }

    this.router.navigateByUrl(targetRoute).catch(() => {
      // Intentionally swallow navigation errors from stale route transitions.
    });
  }

  private getStepRoute(stepIndex: number): string | null {
    if (this.process === 'move') {
      const moveRoutes = [
        Nav.MoveService,
        Nav.MoveServiceAddress,
        Nav.MoveServiceScheduleService,
        Nav.MoveServiceChooseRates,
      ];

      return moveRoutes[stepIndex] ?? null;
    }

    if (this.process === 'start') {
      const startRoutes = [Nav.StartServiceAddress, Nav.StartServiceScheduleService, Nav.StartServiceChooseRates];
      return startRoutes[stepIndex] ?? null;
    }

    if (this.process === 'reconnect') {
      const reconnectRoutes = [Nav.ReconnectService, Nav.ReconnectRates];
      return reconnectRoutes[stepIndex] ?? null;
    }

    return null;
  }
}
